import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface UpdateColumnRequest {
  name?: string;
  description?: string;
  color?: string;
  position?: number;
}

export interface UpdateColumnResponse {
  success: boolean;
  message: string;
}

async function updateColumn(
  columnId: string,
  data: UpdateColumnRequest,
  token: string | undefined,
  workspaceId: string,
) {
  const bearer = resolveBearerToken(token);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const response = await axios.put<UpdateColumnResponse>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets/columns/${columnId}`,
    data,
    { headers },
  );

  return response.data;
}

export function useUpdateColumn() {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const queryClient = useQueryClient();

  // Helper function to extract workspace ID from workspace object
  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3);
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);

  return useMutation({
    mutationFn: ({ columnId, data }: { columnId: string; data: UpdateColumnRequest }) => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }

      return updateColumn(columnId, data, user?.signInUserSession?.idToken?.jwtToken, workspaceId);
    },
    onMutate: async ({ columnId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries([queryKeys.tickets, workspaceId]);

      // Snapshot the previous value
      const previousTickets = queryClient.getQueryData([queryKeys.tickets, workspaceId]);

      // Optimistically update the column name in cache
      queryClient.setQueryData([queryKeys.tickets, workspaceId], (old: any) => {
        if (!old) return old;

        console.log('🔮 Optimistic update: Renaming column', columnId);

        const newColumns = old.columns.map((col: any) => {
          if (col.id === columnId) {
            return { ...col, ...data };
          }
          return col;
        });

        return { ...old, columns: newColumns };
      });

      return { previousTickets };
    },
    onSuccess: async () => {
      console.log('✅ Column updated successfully');
      console.log('🔄 Invalidating tickets query for workspace:', workspaceId);

      // Invalidate tickets query - React Query will refetch and sync with server
      await queryClient.invalidateQueries([queryKeys.tickets, workspaceId]);

      console.log('✅ Query invalidated, refetch will happen automatically');
    },
    onError: (err: any, variables, context: any) => {
      console.error('❌ Column update failed:', err);

      // Rollback to previous state on error
      if (context?.previousTickets) {
        queryClient.setQueryData([queryKeys.tickets, workspaceId], context.previousTickets);
        console.log('🔙 Rolled back to previous state due to error');
      }

      // Handle specific error cases
      if (err.response?.status === 400) {
        throw new Error('Invalid column data. Please check your input and try again.');
      } else if (err.response?.status === 401) {
        throw new Error('Unauthorized. Please log in again.');
      } else if (err.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else if (err.response?.status === 404) {
        throw new Error('Column not found.');
      } else if (err.response?.status === 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(err.message || 'Failed to update column. Please try again.');
      }
    },
  });
}
